<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { page } from "$app/state";
  import { goto } from "$app/navigation";
  import { cn } from "$lib/utils";
  import { searchStore, authStore, searchHistoryStore } from "$lib/stores";
  import { searchFormOverlayStore } from "$lib/stores/searchFormOverlayStore";
  import { SearchAPI } from "$lib/api/search";
  import { SEARCH_PROGRESS } from "$lib/types";
  import type { SearchProgress, SearchInfo } from "$lib/types";

  import AlertMessage from "$lib/components/dialogs/alertDialog.svelte";
  import SubscriptionDialog from "$lib/components/dialogs/subscriptionDialog.svelte";
  import { SearchPolling } from "./searchPolling.svelte";
  import routes from "$lib/routes";
  import {
    CUSTOM_SLUG_SUFFIX,
    SEARCH_RESULTS_BATCH_SIZE,
    SEARCH_RESULTS_BATCH_SIZE_NO_USER,
    ANONYMOUS_USER_ID,
  } from "$lib/stores/searchStore";
  import Search from "./search.svelte";
  import SimpleSignInDialog from "$lib/components/dialogs/simpleSignInDialog.svelte";
  import PanelToggleButton from "$lib/components/layout/sidepanel/panelToggleButton.svelte";
  import { authenticate } from "$lib/auth/authGuard";

  const RESULTS_PER_FETCH = 40;

  import type { SearchResult } from "$lib/types";
  import { sidePanelStore } from "$lib/stores/sidePanelStore";

  let isExpandingSearch = $state(false);
  let localSearchProgress: SearchProgress = $state(SEARCH_PROGRESS.UNDEFINED);
  let alertDialogOpen = $state(false);
  let alertMessage = $state("");
  let subscriptionDialogOpen = $state(false);
  let isLoadingMoreResults = $state(false);
  let isInitialized = $state(false);
  let isLoadingData = $state(false);
  let signInDialogOpen = $state(false);

  const searchPolling = new SearchPolling();

  let slug = $derived(page.params.slug);
  let currentSearch = $derived($searchStore[slug]);
  let isLoading = $derived(!currentSearch?.id);
  let isViewedByOwner = $derived(
    $authStore.isLoggedIn && currentSearch?.userID?.toString() === $authStore.user?.id.toString(),
  );
  let searchResults = $derived<SearchResult[]>(currentSearch?.results || []);
  let isSearchInProgress = $derived(
    currentSearch?.searchStatus === "processing" || currentSearch?.searchStatus === "expanding",
  );

  function resetState() {
    localSearchProgress = SEARCH_PROGRESS.UNDEFINED;
    isLoadingMoreResults = false;
    isExpandingSearch = false;
  }

  // const allResultsDisplayed = $derived(
  //   (currentSearch?.results?.length || 0) >= (currentSearch?.definiteMatch || 0) + (currentSearch?.partialMatch || 0),
  // );

  async function initializeSearch(currentSlug: string) {
    if (!currentSlug) return;

    logger.log("Initialize search:", currentSlug);
    try {
      resetState();

      if (!currentSlug.endsWith(CUSTOM_SLUG_SUFFIX)) {
        const metadata = await SearchAPI.getSearchMetadataBySlug(
          currentSlug,
          $authStore.isLoggedIn,
          $authStore.user?.id,
          true,
        );

        if (!metadata) {
          goto(routes.searches);
          return;
        }

        searchHistoryStore.updateSearch(currentSlug, {
          matchesCount: (metadata.definiteMatch || 0) + (metadata.partialMatch || 0),
          category: metadata.category,
          updatedAt: new Date(),
        });

        searchStore.initializeSearch(currentSlug, {
          ...metadata,
          slug: currentSlug,
          results: [],
          error: null,
          progressStatus: SEARCH_PROGRESS.UNDEFINED,
          status: "visible",
        });

        const results = await SearchAPI.getSearchResultsBySlug(
          currentSlug,
          RESULTS_PER_FETCH,
          0,
          $authStore.isLoggedIn || metadata.isPublic,
          $authStore.user?.id,
        );

        if (results && results.length > 0) {
          searchStore.updateResults(currentSlug, results);
        }

        if (metadata.searchStatus === "processing" || metadata.searchStatus === "expanding") {
          searchPolling.startPolling(
            metadata.id || "",
            currentSlug,
            $authStore.isLoggedIn,
            $authStore.user?.id,
            currentSearch,
          );
        }
      }
    } catch (error) {
      console.error("Error initializing search:", error);
      searchStore.setError(currentSlug, error instanceof Error ? error.message : "Failed to load search");
      if ($authStore.isLoggedIn) {
        goto(routes.searches);
      } else {
        authenticate(page.url.pathname);
      }
    }
  }

  // Effect to handle slug changes and initialization
  $effect(() => {
    const currentSlug = page.params.slug;
    if (!currentSlug) {
      resetState();
      return;
    }

    searchStore.stopSearch(currentSlug);
    searchPolling.stopPolling(currentSlug);

    initializeSearch(currentSlug);
  });

  // Effect to handle search results updates
  $effect(() => {
    if (!currentSearch) return;

    if (currentSearch.results) {
      // Only update if length or first/last items changed (to avoid triggering full refresh)
      // const currentFirst = searchResults[0]?.id;
      // const currentLast = searchResults[searchResults.length - 1]?.id;
      // const newFirst = currentSearch.results[0]?.id;
      // const newLast = currentSearch.results[currentSearch.results.length - 1]?.id;

      // if (
      //   searchResults.length !== currentSearch.results.length ||
      //   currentFirst !== newFirst ||
      //   currentLast !== newLast
      // ) {
      //   searchResults = currentSearch.results;
      // }
      isLoadingMoreResults = false;
    }

    // Handle status updates
    const storedStatus = currentSearch.progressStatus;
    if (storedStatus && !isExpandingSearch) {
      if (!searchStore.isSearchActive(currentSearch.slug) || storedStatus === SEARCH_PROGRESS.IN_PROGRESS) {
        localSearchProgress = storedStatus as SearchProgress;
      }
    }
  });

  onMount(async () => {
    if (currentSearch && currentSearch.slug === slug) {
      isLoadingData = false;
    }
  });

  onDestroy(() => {
    searchStore.stopSearch(slug);
    searchPolling.stopPolling(slug);
  });

  $effect(() => {
    const currentSlug = page.params.slug;
    if (!currentSlug) {
      isLoadingData = true;
      return;
    }

    if (!isInitialized) {
      initializeSearch(currentSlug);
      return;
    }

    const currentSearch = searchStore.getSearch(currentSlug);
    if (!currentSearch) {
      isLoadingData = true;
      return;
    }

    if (currentSearch.results) {
      // searchResults = currentSearch.results;

      isLoadingMoreResults = false;
    }

    // Handle status updates
    const storedStatus = currentSearch.progressStatus;
    if (storedStatus && !isExpandingSearch) {
      if (!searchStore.isSearchActive(currentSlug) || storedStatus === SEARCH_PROGRESS.IN_PROGRESS) {
        localSearchProgress = storedStatus as SearchProgress;
      }
    }
  });

  // async function triggerExpandSearch() {
  //   if (!currentSearch) return;

  //   try {
  //     isExpandingSearch = true;
  //     localSearchProgress = SEARCH_PROGRESS.IN_PROGRESS;
  //     if (currentSearch.id) {
  //       await searchStore.expandSearch(currentSearch.id, currentSearch.slug);
  //     }
  //   } catch (error) {
  //     console.error("Error expanding search:", error);
  //     searchStore.updateSearch(currentSearch.slug, {
  //       ...currentSearch,
  //       progressStatus: SEARCH_PROGRESS.FINISHED,
  //     });
  //     logger.info("Search expanded (error).");
  //   } finally {
  //     isExpandingSearch = false;
  //   }
  // }

  function canExpandSearch(search: SearchInfo): boolean {
    if (!search) return false;

    logger.log("search.findMore", search.findMore);
    logger.log("search.searchStatus", search.searchStatus);
    return (
      search.findMore !== false && search.searchStatus !== "processing" && search.searchStatus !== "expanding"
      // (localSearchProgress === SEARCH_PROGRESS.FINISHED ||
      //   localSearchProgress === SEARCH_PROGRESS.CANCELLED ||
      //   localSearchProgress === SEARCH_PROGRESS.UNDEFINED)
    );
  }

  // async function fetchAllExistingData(slug: string, userId?: string) {
  //   if (!currentSearch) return null;

  //   let meta;
  //   try {
  //     meta = await SearchAPI.getSearchMetadataBySlug(slug, authStore.isLoggedIn(), userId);
  //     if (!meta) {
  //       searchStore.updateSearch(slug, { searchStatus: "completed" });
  //       return;
  //     }
  //   } catch {
  //     searchStore.updateSearch(slug, { searchStatus: "completed" });
  //     return;
  //   }

  //   const totalResults = (meta.definiteMatch || 0) + (meta.partialMatch || 0);
  //   let loadedCount = currentSearch.results?.length || 0;
  //   let attempts = 0;
  //   const maxAttempts = Math.ceil(totalResults / SEARCH_RESULTS_BATCH_SIZE) + 1;

  //   while (loadedCount < totalResults && attempts < maxAttempts) {
  //     try {
  //       const batch = await SearchAPI.getSearchResultsBySlug(
  //         slug,
  //         SEARCH_RESULTS_BATCH_SIZE,
  //         loadedCount,
  //         authStore.isLoggedIn() || meta.isPublic,
  //         userId,
  //       );

  //       if (!batch?.length) break;

  //       // Use the new mergeResults function
  //       searchStore.mergeResults(slug, batch);

  //       const updated = searchStore.getSearch(slug);
  //       if (!updated) break;

  //       // Update history store with latest counts
  //       searchHistoryStore.updateSearch(slug, {
  //         matchesCount: (updated.definiteMatch || 0) + (updated.partialMatch || 0),
  //         updatedAt: new Date(),
  //       });

  //       loadedCount = updated.results?.length || 0;
  //     } catch (error) {
  //       console.error("Error fetching batch:", error);
  //       break;
  //     }
  //     attempts++;

  //     // Add a small delay between batches
  //     await new Promise((resolve) => setTimeout(resolve, 100));
  //   }

  //   return meta;
  // }

  async function handleFindMore() {
    if (!canExpandSearch(currentSearch)) {
      return;
    }

    const slug = currentSearch.slug;
    const userId = currentSearch.userID;

    try {
      isExpandingSearch = true;
      localSearchProgress = SEARCH_PROGRESS.IN_PROGRESS;

      // const meta = await fetchAllExistingData(slug, userId);
      // if (!meta) return;

      await searchStore.expandSearchWithoutEvents(currentSearch.id || "", slug, userId);

      if (
        currentSearch &&
        (currentSearch.searchStatus === "expanding" || currentSearch.searchStatus === "processing")
      ) {
        searchPolling.startPolling(
          currentSearch.id || "",
          slug,
          $authStore.isLoggedIn,
          $authStore.user?.id,
          currentSearch,
        );
      }
    } catch (error) {
      console.error("Error in handleFindMore:", error);
      searchStore.setError(slug, "Failed to expand search");
    } finally {
      isExpandingSearch = false;
    }
  }

  async function handleFindAll() {}

  async function handleFetchMore(e: CustomEvent) {
    if (isLoadingMoreResults || !currentSearch) return;

    // if (!$authStore.isLoggedIn && currentSearch?.userID == ANONYMOUS_USER_ID) {
    //   // subscriptionDialogOpen = true;
    //   return;
    // }

    isLoadingMoreResults = true;
    try {
      const nextResults = await SearchAPI.getSearchResultsBySlug(
        currentSearch.slug,
        RESULTS_PER_FETCH,
        currentSearch.results?.length || 0,
        $authStore.isLoggedIn || currentSearch.isPublic,
        $authStore.user?.id,
      );

      if (nextResults.length > 0) {
        await searchStore.updateResults(currentSearch.slug, [...(currentSearch.results || []), ...nextResults]);
      }
    } catch (error) {
      console.error("Error loading more results:", error);
    } finally {
      isLoadingMoreResults = false;
    }
  }

  // async function handleFetchMore() {
  //   if (isLoadingMoreResults || isExpandingSearch || !currentSearch) return;

  //   if (!$authStore.isLoggedIn) {
  //     subscriptionDialogOpen = true;
  //     return;
  //   }

  //   isLoadingMoreResults = true;
  //   try {
  //     // First, get the current metadata to verify total count
  //     const metadata = await SearchAPI.getSearchMetadataBySlug(
  //       currentSearch.slug,
  //       $authStore.isLoggedIn,
  //       $authStore.user?.id,
  //     );

  //     const totalExpectedResults = (metadata?.definiteMatch || 0) + (metadata?.partialMatch || 0);
  //     const currentResultsCount = currentSearch.results?.length || 0;

  //     // Only fetch if we don't have all results
  //     if (currentResultsCount < totalExpectedResults) {
  //       const nextResults = await SearchAPI.getSearchResultsBySlug(
  //         currentSearch.slug,
  //         RESULTS_PER_FETCH,
  //         currentResultsCount,
  //         $authStore.isLoggedIn || currentSearch.isPublic,
  //         $authStore.user?.id,
  //       );

  //       if (nextResults.length > 0) {
  //         await searchStore.updateResults(currentSearch.slug, nextResults);

  //         // Force a refresh of the displayed results
  //         searchResults = searchStore.getSearch(currentSearch.slug)?.results || [];
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error loading more results:", error);
  //   } finally {
  //     isLoadingMoreResults = false;
  //   }
  // }

  function handleAlert(message: string) {
    alertMessage = message;
    alertDialogOpen = true;
  }
</script>

{#if isLoading}
  <div class="flex h-screen w-full items-center justify-center">
    <div
      class="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"
      role="status"
      aria-label="Loading"
    >
      <span class="sr-only">Loading...</span>
    </div>
  </div>
{:else}
  <div class={cn("results-outer-wrapper relative flex h-full flex-1 flex-col items-center")}>
    <div
      class="results-wrapper custom-scrollbar transition-default flex h-full w-full flex-1 flex-col overflow-y-hidden"
    >
      <div class="flex h-full w-full">
        <Search
          {isLoadingData}
          {isSearchInProgress}
          {isLoadingMoreResults}
          {currentSearch}
          {searchResults}
          {isViewedByOwner}
          bind:subscriptionDialogOpen
          showSignInPrompt={() => (signInDialogOpen = true)}
          on:fetchMore={handleFetchMore}
          on:findMore={handleFindMore}
          on:findAll={handleFindAll}
          on:alert={(e) => handleAlert(e.detail)}
        />
      </div>
    </div>
  </div>
{/if}

<AlertMessage bind:open={alertDialogOpen} title="Oh no!" message={alertMessage} />
<SubscriptionDialog bind:open={subscriptionDialogOpen} />
<SimpleSignInDialog bind:open={signInDialogOpen} />
<!-- <PanelToggleButton /> -->
